import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import { useOrgContext } from '@/hooks/useOrgContext';
import { usePolicyFilters } from '@/hooks/usePolicyFilters';
import { usePolicySelection } from '@/hooks/usePolicySelection';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useTableSort } from '@/hooks/useTableSort';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Plus, Trash2, Upload } from 'lucide-react';
import { PolicyTableHeader } from '@/components/insurance/PolicyTableHeader';
import { PolicyTableRow } from '@/components/insurance/PolicyTableRow';
import { PolicyTableSkeleton } from '@/components/insurance/PolicyTableSkeleton';
import { DeletePoliciesDialog } from '@/components/insurance/DeletePoliciesDialog';
import { ImportPolicyDialog } from '@/components/insurance/ImportPolicyDialog';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { POLICIES_COLUMNS } from '@/lib/tableColumns';
import type { IInsurancePolicy, IOrgInsuranceType, TInvoiceStatus } from '@/@types/insurance';

function toPolicyList(
  raw:
    | {
        _id: IInsurancePolicy['_id'];
        orgId: IInsurancePolicy['orgId'];
        insuranceTypeId: IInsurancePolicy['insuranceTypeId'];
        policyNumber: string;
        customerName?: string;
        createdBy: IInsurancePolicy['createdBy'];
        creatorName?: string;
        status: IInsurancePolicy['status'];
        insuranceTypeName?: string;
        issuedAt?: number;
        expiresAt?: number;
        invoiceStatus?: TInvoiceStatus;
        providerName?: string;
        createdAt: number;
        updatedAt: number;
      }[]
    | undefined
): IInsurancePolicy[] {
  if (!raw) return [];
  return raw.map(p => ({ ...p, fieldValues: [] }));
}

function policyAccessor(policy: IInsurancePolicy, columnId: string): string | number {
  switch (columnId) {
    case 'policyNumber':
      return policy.policyNumber;
    case 'customer':
      return policy.customerName ?? '';
    case 'provider':
      return policy.providerName ?? '';
    case 'type':
      return policy.insuranceTypeName ?? '';
    case 'status':
      return policy.status;
    case 'premium':
      return policy.premium ?? 0;
    case 'invoice':
      return policy.invoiceStatus ?? 'not_paid';
    case 'createdBy':
      return policy.creatorName ?? '';
    case 'issued':
      return policy.issuedAt ?? policy.createdAt;
    case 'expires':
      return policy.expiresAt ?? 0;
    default:
      return '';
  }
}

export function PoliciesListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const [isImportOpen, setIsImportOpen] = useState(false);

  const updateInvoiceStatus = useMutation(api.insurance.policies.updateInvoiceStatus);

  const handleInvoiceStatusChange = useCallback(
    (policyId: IInsurancePolicy['_id'], status: TInvoiceStatus) => {
      void updateInvoiceStatus({ policyId, invoiceStatus: status });
    },
    [updateInvoiceStatus]
  );

  const rawPolicies = useQuery(
    api.insurance.policies.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const orgTypes = useQuery(
    api.insurance.types.listOrgTypes,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const isLoading = orgLoading || rawPolicies === undefined;
  const policies = toPolicyList(rawPolicies ?? undefined);
  const typeList: IOrgInsuranceType[] = orgTypes ?? [];

  const { visibleColumns, allColumns, isColumnVisible, toggleColumn, resetToDefaults } =
    useColumnVisibility('insurance-policies', POLICIES_COLUMNS);

  const { filters, filteredPolicies, dispatchFilter } = usePolicyFilters(policies);

  const {
    sortState,
    toggleSort,
    sortedItems: sortedPolicies,
  } = useTableSort(
    filteredPolicies,
    POLICIES_COLUMNS,
    { defaultSort: { sortBy: 'issued', direction: 'desc' } },
    policyAccessor
  );

  const {
    selectedIds,
    isDeleteOpen,
    setIsDeleteOpen,
    handleSelectRow,
    handleSelectAll,
    handleDeleteConfirm,
    isAllSelected,
    isSomeSelected,
  } = usePolicySelection(sortedPolicies);

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage insurance policies.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary pt-0 pb-8 px-10">
      <div className="sticky top-0 z-20 bg-background-secondary/95 backdrop-blur-sm py-6 -mx-10 px-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Policies</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isLoading ? 'Loading...' : `${policies.length} total`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
                <Trash2 aria-hidden="true" className="w-4 h-4 mr-2" />
                Delete ({selectedIds.size})
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/insurance/renewal-import')}>
              <Upload aria-hidden="true" className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button variant="outline" onClick={() => setIsImportOpen(true)}>
              <Upload aria-hidden="true" className="w-4 h-4 mr-2" />
              Import Policy
            </Button>
            <Button onClick={() => navigate('/insurance/policies/new')}>
              <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
              New Policy
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="policy-search" className="sr-only">
            Search policies
          </label>
          <Input
            id="policy-search"
            placeholder="Search by policy # or customer..."
            value={filters.searchQuery}
            onChange={e => dispatchFilter({ type: 'SET_SEARCH', query: e.target.value })}
            className="max-w-xs"
          />
          <label htmlFor="policy-type-filter" className="sr-only">
            Filter by type
          </label>
          <Select
            value={filters.typeFilter}
            onValueChange={val => dispatchFilter({ type: 'SET_TYPE_FILTER', typeFilter: val })}
          >
            <SelectTrigger id="policy-type-filter" className="w-[180px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {typeList.map(t => (
                <SelectItem key={t._id} value={t._id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label htmlFor="policy-status-filter" className="sr-only">
            Filter by status
          </label>
          <Select
            value={filters.statusFilter}
            onValueChange={val => dispatchFilter({ type: 'SET_STATUS_FILTER', statusFilter: val })}
          >
            <SelectTrigger id="policy-status-filter" className="w-[160px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <label htmlFor="policy-invoice-filter" className="sr-only">
            Filter by invoice
          </label>
          <Select
            value={filters.invoiceFilter}
            onValueChange={val =>
              dispatchFilter({ type: 'SET_INVOICE_FILTER', invoiceFilter: val })
            }
          >
            <SelectTrigger id="policy-invoice-filter" className="w-[180px]">
              <SelectValue placeholder="All invoices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All invoices</SelectItem>
              <SelectItem value="not_paid">Not paid</SelectItem>
              <SelectItem value="partially_paid">Partially paid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto">
            <ColumnSettingsPopover
              allColumns={allColumns}
              isColumnVisible={isColumnVisible}
              toggleColumn={toggleColumn}
              resetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <PolicyTableHeader
            sortState={sortState}
            onToggleSort={toggleSort}
            isAllSelected={isAllSelected}
            isSomeSelected={isSomeSelected}
            onSelectAllChange={handleSelectAll}
            visibleColumns={visibleColumns}
          />
          {isLoading && <PolicyTableSkeleton />}
          {!isLoading &&
            sortedPolicies.map(policy => (
              <PolicyTableRow
                key={policy._id}
                policy={policy}
                isSelected={selectedIds.has(policy._id)}
                onSelectChange={selected => handleSelectRow(policy._id, selected)}
                onInvoiceStatusChange={status => handleInvoiceStatusChange(policy._id, status)}
                visibleColumns={visibleColumns}
                isColumnVisible={isColumnVisible}
              />
            ))}
          {!isLoading && sortedPolicies.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No policies found</h3>
              <p className="text-sm text-muted-foreground">
                {filters.searchQuery ||
                filters.typeFilter !== 'all' ||
                filters.statusFilter !== 'all' ||
                filters.invoiceFilter !== 'all'
                  ? 'Try adjusting your filters or search query'
                  : 'Create your first policy to get started'}
              </p>
            </div>
          )}
        </div>
      </div>

      {currentOrg && (
        <ImportPolicyDialog
          open={isImportOpen}
          onOpenChange={setIsImportOpen}
          orgId={currentOrg._id}
        />
      )}
      <DeletePoliciesDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        count={selectedIds.size}
      />
    </div>
  );
}
